import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useInterpret } from '@xstate/react';
import { format } from 'date-fns';

import { useNoticeBannersStackContext } from '@sg/garnish';

import { useAppsFlyer } from '@order/integrations-appsflyer';
import { useCreateRadarSession } from '@order/integrations-stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import {
  checkIfAccessTokenHasExpired,
  refreshAccessTokens,
} from '../../../../../context/AzureAuth';
import { getFetchOptions } from '../../../../../context/Urql';
import { useCreateAccountMutation } from '../../../graphql/CreateAccount.generated';
import { useSignInMutation } from '../../../graphql/SignIn.generated';
import {
  joinOrSignInStateMachine,
  type JoinOrSignInStateMachineContext,
  type JoinOrSignInStateMachineEvents,
  type JoinOrSignInStateMachineServices,
} from './join-or-sign-in-machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useJoinOrSignInService = () => {
  const { formatMessage } = useIntl();
  const { push: addBanner } = useNoticeBannersStackContext();

  const { track } = useTelemetry();
  const { logCompleteRegistrationEvent } = useAppsFlyer();

  const isRadarRegistrationEvaluationEnabled = useFeatureFlag(
    'CELS-5659-radar-registration-evaluation-enabled',
  );

  // ─── Radar ───────────────────────────────────────────────────────────

  const createRadarSession = useCreateRadarSession();

  // ─── Network Requests ────────────────────────────────────────────────

  const [, executeSignIn] = useSignInMutation();
  const [, executeCreateAccount] = useCreateAccountMutation();

  // ─── Actions ─────────────────────────────────────────────────────────

  const onErrorMessage = useCallback(
    (
      _context: JoinOrSignInStateMachineContext,
      event: Extract<JoinOrSignInStateMachineEvents, { type: 'ERROR' }>,
    ) => {
      if (!event.text) return;

      addBanner({ palette: 'caution', text: event.text });
    },
    [addBanner],
  );

  const onSuccessMessage = useCallback(
    (
      _context: JoinOrSignInStateMachineContext,
      event: Extract<JoinOrSignInStateMachineEvents, { type: 'SUCCESS' }>,
    ) => {
      if (!event.text) return;

      addBanner({ palette: 'success', text: event.text });
    },
    [addBanner],
  );

  // ─── Services ────────────────────────────────────────────────────────

  const signIn = useCallback(
    async (
      context: JoinOrSignInStateMachineContext,
    ): Promise<JoinOrSignInStateMachineServices['signIn']['data']> => {
      const { accessToken } = context;

      if (!accessToken) {
        throw new Error('Sign in failed | No access tokens');
      }

      const response = await executeSignIn(
        {},
        {
          fetchOptions: getFetchOptions({
            extraHeaders: {
              [API_ACCESS_TOKEN_HEADER]: accessToken,
            },
          }),
        },
      );
      const { data, error } = response;
      const responseType = data?.signIn.__typename;

      const hasUserError = responseType === 'ValidationError' || responseType === 'SignInFailed';
      const systemError = hasUserError ? data?.signIn.message : error?.message;

      if (responseType === 'SignInSuccess') {
        return { hasSignedIn: true };
      }

      if (responseType === 'CustomerNotFound') {
        return { hasSignedIn: false };
      }

      throw new Error(systemError);
    },
    [executeSignIn],
  );

  const createAccount = useCallback(
    async (
      context: JoinOrSignInStateMachineContext,
    ): Promise<JoinOrSignInStateMachineServices['createAccount']['data']> => {
      const { email, accessToken: token, createAccountInput } = context;
      const input = createAccountInput;

      if (!token || !input) {
        return { hasCreatedAccount: false };
      }

      const radarSession = isRadarRegistrationEvaluationEnabled
        ? await createRadarSession()
        : undefined;

      const response = await executeCreateAccount(
        { input: { ...input, radarSession } },
        {
          fetchOptions: getFetchOptions({
            extraHeaders: {
              [API_ACCESS_TOKEN_HEADER]: token,
            },
          }),
        },
      );

      const data = response.data?.createAccount;
      const hasCreatedAccount = data?.__typename === 'CreateAccountSuccess';

      // ─── Error Handling ──────────────────────────────────────────────────

      if (!hasCreatedAccount || response.error) {
        const hasValidationErrors = data?.__typename === 'ValidationError';

        const validationError = hasValidationErrors ? data.fieldErrors?.[0]?.message : undefined;
        const userError = validationError ?? formatMessage(messages.joinError);
        const systemError = response.error?.message;

        addBanner({ text: userError, palette: 'caution' });

        track('join.failure', { userError, systemError });

        return { hasCreatedAccount };
      }

      // ─────────────────────────────────────────────────────────────────────

      track('join.success', {
        name: `${input.firstName} ${input.lastName}`,
        email,
        date: format(new Date(), 'M/dd/yyyy K:mmbbb'),
        referred: false,
      });

      logCompleteRegistrationEvent();

      return { hasCreatedAccount };
    },
    [
      addBanner,
      createRadarSession,
      executeCreateAccount,
      formatMessage,
      isRadarRegistrationEvaluationEnabled,
      logCompleteRegistrationEvent,
      track,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useInterpret(joinOrSignInStateMachine, {
    actions: {
      //
      // ─── Debug Message Loggers ───────────────────────────

      onDebugMessage(_context, event) {
        track('join-or-sign-in.embedded-frame.debug', {
          data: stringifyPayload(event?.data),
        });
      },

      // ─── Error Handlers ──────────────────────────────────

      onInitError(_context, event) {
        track('join-or-sign-in.failure', {
          flow: 'embedded',
          reason: 'Initialization error',
          systemError: stringifyPayload(event?.data),
        });
      },
      onInitTimeoutError(_context) {
        track('join-or-sign-in.failure', {
          flow: 'embedded',
          reason: 'Initialization timeout error',
        });
      },
      onVerificationScreenLoadingTimeout(_context) {
        track('join-or-sign-in.failure', {
          flow: 'embedded',
          reason: 'Verification screen loading timeout error',
        });
      },
      onVerificationTimeout(_context) {
        track('join-or-sign-in.failure', {
          flow: 'embedded',
          reason: 'Code verification timeout error',
        });
      },
      onSignInSystemError(_context, event) {
        track('join-or-sign-in.failure', {
          flow: 'embedded',
          reason: 'Sign-in mutation error',
          systemError: stringifyPayload(event?.data),
        });
      },
      onRefreshAccessTokensSystemError(_context, event) {
        track('join-or-sign-in.failure', {
          flow: 'embedded',
          reason: 'Refresh access tokens error',
          systemError: stringifyPayload(event?.data),
        });
      },
      onCreateAccountSystemError(_context, event) {
        track('join-or-sign-in.failure', {
          flow: 'embedded',
          reason: 'Create account mutation error',
          systemError: stringifyPayload(event?.data),
        });
      },

      // ─── Post Message Handlers ───────────────────────────

      onErrorMessage,
      onSuccessMessage,
    },
    services: {
      createAccount,
      signIn,
      async refreshAccessTokens(context) {
        return refreshAccessTokens(context.refreshToken);
      },
    },
    guards: {
      checkIfNeedToRefreshTokens(context) {
        const { accessToken } = context;

        if (!accessToken) return true;

        return checkIfAccessTokenHasExpired({
          accessToken,
          safeGuardMinutes: 5,
        });
      },
    },
  });
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  joinError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Join | Error',
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function stringifyPayload(payload: unknown): string {
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return payload as string;
  }
}

// ─── Constants ───────────────────────────────────────────────────────────────

const API_ACCESS_TOKEN_HEADER = 'ApiAuthorizationToken';
